"use client";

import { useState } from "react";
import { sendGAEvent } from "@next/third-parties/google";
import { ModelsCardMedia, SectionsCardGrid } from "@/types";
import { getMediaType } from "@/util/media";
import { useApp } from "@/hooks/use-app";

import {
  resourceItemVariant,
  type ResourceItemVariantProps,
} from "./resource-item.variants";
import { cardMediaTextSize } from "./resource-item.text-size";
import { cardMediaIcons } from "./resource-item.icons";

export type UseResourceItemProps = ResourceItemVariantProps &
  Pick<SectionsCardGrid, "aspectRatio"> & {
    style?: Record<string, string | number>;
    data: ModelsCardMedia;
    appearance: "grid" | "inline";
  };

export const useResourceItem = (props: UseResourceItemProps) => {
  const {
    data: { media, link, icon, asset, ...restData },
    aspectRatio,
    appearance,
  } = props;
  const { onVerifyConsent } = useApp();

  const [isLoading, setIsLoading] = useState(false);

  const styles = resourceItemVariant({
    aspectRatio,
    appearance,
    isLoading,
  });

  const isVideoBackground = !!media && getMediaType(media?.url) === "video";
  const textSize = cardMediaTextSize[appearance];
  const iconName = icon || cardMediaIcons[appearance];
  const hasCustomIcon = !!icon;
  const isDownloadButton = !!asset;
  const finalLink = isDownloadButton ? asset?.url : link;

  const handleVerifyConsent = async () => {
    try {
      setIsLoading(true);
      await onVerifyConsent(restData?.documentId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!restData?.documentId) return;

    sendGAEvent("event", "resource_downloaded", {
      resourceId: restData?.documentId,
      resourceName: restData?.title,
    });

    if (restData?.private) {
      handleVerifyConsent();
    } else {
      window.open(finalLink, "_blank");
    }
  };

  return {
    styles,
    data: { media, link: finalLink, ...restData },
    appearance,
    textSize,
    iconName,
    isVideoBackground,
    hasCustomIcon,
    onClick: handleDownload,
    isLoading,
  };
};
